import { Box, Typography, Button, Paper, keyframes } from '@mui/material'
import SecurityIcon from '@mui/icons-material/Security'
import LogoutIcon from '@mui/icons-material/Logout'
import { useEffect, useState } from 'react'
import KeycloakService from '../utils/KeycloakService'

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
`

/* Using Sx as this page is out of themeing */

const NoAccess = () => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleLogout = async () => {
    await KeycloakService?.doLogout?.()?.catch((error) => {
      console.error('Logout error:', error)
    })
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        padding: { xs: 2, sm: 3, md: 4 },
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'auto',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: { xs: '100%', sm: 480, md: 520 },
          width: '100%',
          padding: { xs: 3, sm: 4, md: 5 },
          textAlign: 'center',
          borderRadius: { xs: 2, sm: 3 },
          animation: mounted ? `${fadeIn} 0.5s ease-out` : 'none',
          position: 'relative',
        }}
      >
        {/* Shield Icon */}
        <Box
          sx={{
            width: { xs: 64, sm: 72, md: 80 },
            height: { xs: 64, sm: 72, md: 80 },
            margin: '0 auto',
            marginBottom: { xs: 2, sm: 2.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: '#ffebee',
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        >
          <SecurityIcon
            sx={{
              fontSize: { xs: 36, sm: 40, md: 48 },
              color: '#d32f2f',
            }}
          />
        </Box>

        {/* Access Denied Title */}
        <Typography
          variant='h4'
          component='h1'
          sx={{
            fontWeight: 600,
            color: '#212121',
            marginBottom: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          }}
        >
          Access Denied
        </Typography>

        {/* Description */}
        <Typography
          variant='body1'
          sx={{
            color: '#616161',
            marginBottom: { xs: 3, sm: 3.5, md: 4 },
            lineHeight: 1.6,
            fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
            px: { xs: 0, sm: 2 },
          }}
        >
          You don't have permission to access this page. Please contact your
          administrator if you believe this is an error.
        </Typography>

        {/* Log Out Button */}
        <Button
          variant='contained'
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          sx={{
            textTransform: 'none',
            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
            fontWeight: 500,
            padding: { xs: '10px 28px', sm: '11px 32px', md: '12px 36px' },
            borderRadius: { xs: 1.5, sm: 2 },
            backgroundColor: '#1976d2',
            color: '#ffffff',
            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
            minWidth: { xs: 140, sm: 160 },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: 200,
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Log Out
        </Button>

        {/* Need Help Link */}
        <Box
          sx={{
            marginTop: { xs: 3, sm: 3.5, md: 4 },
            paddingTop: { xs: 2.5, sm: 3 },
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant='body2'
            sx={{
              color: '#757575',
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
            }}
          >
            Need help?{' '}
            <Typography
              component='span'
              sx={{
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'none',
                fontSize: 'inherit',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Contact support
            </Typography>
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export default NoAccess
